import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from 'lucide-react';
import Switch from '../../../components/Switch';
import { getIframeConfig, updateIframeConfig, setIframeSecret, clearIframeSecret } from '../../../utils/queries';

// Iframe store settings (storefront_type 'iframe'): how the host app launches
// the storefront, the member fields signed into its JWT (which the "View Store"
// test page renders as its form), and the JWT secret shared with the
// storefront's JWT_SECRET. PLATFORMADMIN only (route + API).

const FIELD_TYPES = [
	{ value: 'text', label: 'Text' },
	{ value: 'email', label: 'Email' },
	{ value: 'integer', label: 'Whole number' },
];
const DEFAULT_FROM = [
	{ value: '', label: '—' },
	{ value: 'userEmail', label: 'My email' },
	{ value: 'userFullName', label: 'My name' },
];
const EMPTY_FIELD = { key: '', label: '', type: 'text', group: '', defaultValue: '', defaultFrom: '', required: false };

const toForm = (config) => ({
	launchPath: config?.launchPath || '/session-init',
	tokenParam: config?.tokenParam || 'token',
	tokenTtlMinutes: String(config?.tokenTtlMinutes ?? 60),
	fields: (config?.fields || []).map((f) => ({ ...EMPTY_FIELD, ...f, defaultFrom: f.defaultFrom || '' })),
});

export default function IframeTab({ store }) {
	const storeId = store.storeId;
	const [data, setData] = useState(null);
	const [form, setForm] = useState(toForm(null));
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const [secretInput, setSecretInput] = useState('');
	const [secretBusy, setSecretBusy] = useState(false);
	const [generated, setGenerated] = useState('');

	const load = () =>
		getIframeConfig(storeId).then((result) => {
			if (!result?.success) {
				setError(result?.error?.message || 'Failed to load iframe settings.');
				return;
			}
			setData(result.data);
			setForm(toForm(result.data.config));
		});

	useEffect(() => {
		setGenerated('');
		setSecretInput('');
		setError('');
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId]);

	const setField = (index, patch) =>
		setForm((current) => ({
			...current,
			fields: current.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
		}));

	const moveField = (index, delta) =>
		setForm((current) => {
			const target = index + delta;
			if (target < 0 || target >= current.fields.length) return current;
			const fields = [...current.fields];
			[fields[index], fields[target]] = [fields[target], fields[index]];
			return { ...current, fields };
		});

	const removeField = (index) =>
		setForm((current) => ({ ...current, fields: current.fields.filter((_, i) => i !== index) }));

	const addField = () =>
		setForm((current) => ({
			...current,
			fields: [...current.fields, { ...EMPTY_FIELD, group: current.fields.at(-1)?.group || '' }],
		}));

	const handleSave = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		const result = await updateIframeConfig(storeId, {
			...form,
			tokenTtlMinutes: Number(form.tokenTtlMinutes),
			fields: form.fields.map((f) => ({ ...f, defaultFrom: f.defaultFrom || null })),
		});
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save iframe settings.');
			return;
		}
		setData(result.data);
		setForm(toForm(result.data.config));
		toast.success('Successfully saved your changes.');
	};

	const saveSecret = async (body) => {
		const replacing = data?.secret?.configured;
		if (
			replacing &&
			!window.confirm("Replace this store's JWT secret? The storefront's JWT_SECRET must be updated to match, or members can't sign in.")
		) {
			return;
		}
		setSecretBusy(true);
		const result = await setIframeSecret(storeId, body);
		setSecretBusy(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to save the secret.');
			return;
		}
		setSecretInput('');
		setGenerated(result.data.secret || '');
		toast.success('JWT secret saved.');
		load();
	};

	const removeSecret = async () => {
		if (!window.confirm("Remove this store's JWT secret? The View Store test page will stop working until a new one is saved.")) {
			return;
		}
		setSecretBusy(true);
		const result = await clearIframeSecret(storeId);
		setSecretBusy(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to remove the secret.');
			return;
		}
		setGenerated('');
		load();
	};

	const copyGenerated = () =>
		navigator.clipboard?.writeText(generated).then(
			() => toast.success('Copied.'),
			() => toast.error('Copy failed - select and copy it manually.'),
		);

	if (!data) {
		return error ? <div className='text-sm text-pi-red'>{error}</div> : <div className='text-sm text-gray-400'>Loading...</div>;
	}

	if (data.storefrontType !== 'iframe') {
		return (
			<div className='max-w-2xl text-sm text-gray-600'>
				This store is a <strong>standalone</strong> storefront. To embed it in a host app that signs members in
				with a JWT, change its Storefront Type to Iframe in{' '}
				<Link to='/settings' className='underline'>
					Store - General
				</Link>
				.
			</div>
		);
	}

	const secret = data.secret || {};

	return (
		<div className='max-w-5xl space-y-10'>
			<form onSubmit={handleSave} className='space-y-6'>
				{error && <div className='text-sm text-pi-red'>{error}</div>}

				<section className='space-y-4'>
					<h2 className='text-lg font-light'>Launch</h2>
					<p className='text-xs text-gray-400'>
						The host app opens{' '}
						<code>
							{data.storefrontUrl || 'https://<store domain>'}
							{form.launchPath}?{form.tokenParam}=&lt;JWT&gt;
						</code>{' '}
						in an iframe.
					</p>
					<div className='grid grid-cols-3 gap-4 max-w-2xl'>
						<div>
							<label>Launch path</label>
							<input
								className='form-input'
								value={form.launchPath}
								onChange={(e) => setForm({ ...form, launchPath: e.target.value })}
							/>
						</div>
						<div>
							<label>Token parameter</label>
							<input
								className='form-input'
								value={form.tokenParam}
								onChange={(e) => setForm({ ...form, tokenParam: e.target.value })}
							/>
						</div>
						<div>
							<label>Token lifetime (minutes)</label>
							<input
								className='form-input'
								type='number'
								min='1'
								max='1440'
								value={form.tokenTtlMinutes}
								onChange={(e) => setForm({ ...form, tokenTtlMinutes: e.target.value })}
							/>
						</div>
					</div>
				</section>

				<section className='space-y-3'>
					<h2 className='text-lg font-light'>Member fields</h2>
					<p className='text-xs text-gray-400'>
						Signed into the JWT and shown as the View Store test page's form. Use dots in a key for nested values
						(e.g. <code>address.city</code>). Fields with the same group share a row on the test page.
					</p>
					<table className='w-full text-sm'>
						<thead>
							<tr className='text-left text-xs text-gray-500'>
								<th className='py-1 pr-2 font-normal'>Key</th>
								<th className='py-1 pr-2 font-normal'>Label</th>
								<th className='py-1 pr-2 font-normal'>Type</th>
								<th className='py-1 pr-2 font-normal'>Group</th>
								<th className='py-1 pr-2 font-normal'>Default</th>
								<th className='py-1 pr-2 font-normal'>Default from</th>
								<th className='py-1 pr-2 font-normal'>Required</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{form.fields.map((field, index) => (
								<tr key={index} className='align-middle'>
									<td className='py-1 pr-2'>
										<input
											className='form-input'
											value={field.key}
											onChange={(e) => setField(index, { key: e.target.value })}
											placeholder='memberId'
										/>
									</td>
									<td className='py-1 pr-2'>
										<input
											className='form-input'
											value={field.label}
											onChange={(e) => setField(index, { label: e.target.value })}
										/>
									</td>
									<td className='py-1 pr-2'>
										<select
											className='form-select w-full'
											value={field.type}
											onChange={(e) => setField(index, { type: e.target.value })}
										>
											{FIELD_TYPES.map((t) => (
												<option key={t.value} value={t.value}>
													{t.label}
												</option>
											))}
										</select>
									</td>
									<td className='py-1 pr-2'>
										<input
											className='form-input'
											value={field.group}
											onChange={(e) => setField(index, { group: e.target.value })}
										/>
									</td>
									<td className='py-1 pr-2'>
										<input
											className='form-input'
											value={field.defaultValue}
											onChange={(e) => setField(index, { defaultValue: e.target.value })}
										/>
									</td>
									<td className='py-1 pr-2'>
										<select
											className='form-select w-full'
											value={field.defaultFrom}
											onChange={(e) => setField(index, { defaultFrom: e.target.value })}
										>
											{DEFAULT_FROM.map((d) => (
												<option key={d.value} value={d.value}>
													{d.label}
												</option>
											))}
										</select>
									</td>
									<td className='py-1 pr-2'>
										<Switch checked={field.required} onChange={(checked) => setField(index, { required: checked })} />
									</td>
									<td className='py-1 whitespace-nowrap text-gray-500'>
										<button type='button' className='p-1 hover:text-black' onClick={() => moveField(index, -1)} aria-label='Move up'>
											<ArrowUp className='size-4' />
										</button>
										<button type='button' className='p-1 hover:text-black' onClick={() => moveField(index, 1)} aria-label='Move down'>
											<ArrowDown className='size-4' />
										</button>
										<button type='button' className='p-1 hover:text-pi-red' onClick={() => removeField(index)} aria-label='Remove field'>
											<Trash2 className='size-4' />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{form.fields.length === 0 && <p className='text-sm text-gray-400'>No fields yet.</p>}
					<button type='button' className='btn-secondary flex items-center gap-1.5' onClick={addField}>
						<Plus className='size-4' />
						Add field
					</button>
				</section>

				<div>
					<button type='submit' className='btn-primary' disabled={saving}>
						{saving ? 'Saving...' : 'Save'}
					</button>
				</div>
			</form>

			<section className='space-y-3 max-w-2xl'>
				<h2 className='text-lg font-light'>JWT secret</h2>
				<p className='text-xs text-gray-400'>
					Shared with the storefront: it must equal the storefront's <code>JWT_SECRET</code>. Stored encrypted and
					never shown again after saving.
				</p>
				<p className='text-sm'>
					{secret.configured ? (
						<>
							<span className='font-medium'>Set</span>, ending <code>••••{secret.last4}</code>
							{secret.updatedDate && (
								<span className='text-gray-400'> · updated {new Date(secret.updatedDate).toLocaleString()}</span>
							)}
						</>
					) : (
						<span className='text-pi-red'>Not set - the View Store test page can't sign members in.</span>
					)}
				</p>

				{generated && (
					<div className='p-4 border border-green-200 bg-green-50 text-sm'>
						<p className='font-medium text-green-800 normal-case mb-1'>
							Secret generated - copy it now into the storefront's .env as JWT_SECRET, it will not be shown again:
						</p>
						<div className='flex items-center gap-2'>
							<code className='block break-all text-xs'>{generated}</code>
							<button type='button' className='p-1 text-gray-600 hover:text-black' onClick={copyGenerated} aria-label='Copy'>
								<Copy className='size-4' />
							</button>
						</div>
					</div>
				)}

				<div className='flex items-center gap-2'>
					<input
						className='form-input flex-1'
						type='password'
						autoComplete='new-password'
						placeholder="Paste the storefront's JWT_SECRET"
						value={secretInput}
						onChange={(e) => setSecretInput(e.target.value)}
					/>
					<button
						type='button'
						className='btn-primary'
						disabled={secretBusy || !secretInput.trim()}
						onClick={() => saveSecret({ secret: secretInput })}
					>
						Save secret
					</button>
				</div>
				<div className='flex items-center gap-2'>
					<button
						type='button'
						className='btn-secondary'
						disabled={secretBusy}
						onClick={() => saveSecret({ generate: true })}
					>
						Generate new secret
					</button>
					{secret.configured && (
						<button type='button' className='btn-secondary' disabled={secretBusy} onClick={removeSecret}>
							Remove secret
						</button>
					)}
				</div>
			</section>
		</div>
	);
}
